import { isNotNullable } from '@spendesk/general-type-helpers/misc';
import { type Edge } from '@xyflow/react';

import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import { type CustomFieldDefinition } from 'src/core/modules/budgets/models/customFieldDefinition';
import { TypeGuardError } from 'src/core/utils/switchGuard';

import {
  type ApprovalWorkflowExport,
  type ApprovalWorkflowBranchExport,
  type ApprovalSchemeExport,
} from './approvalWorkflowExportTypes';
import { exportApprovalScheme } from './exportApprovalScheme';
import { type DimensionName, type Dimension } from '../../approvalWorkflow';
import { type ApprovalSchemeWithMemberNames } from '../../hooks/useApprovalSchemesQuery';
import { type CustomNode } from '../../node';

const getApprovalSchemeForNode = ({
  schemeId,
  approvalScheme,
  approvalSchemesById,
}: {
  schemeId?: string;
  approvalScheme?: ApprovalSchemeWithMemberNames;
  approvalSchemesById: Map<string, ApprovalSchemeWithMemberNames>;
}): ApprovalSchemeWithMemberNames | undefined => {
  if (approvalScheme) {
    return approvalScheme;
  }
  if (!schemeId) {
    return undefined;
  }
  return approvalSchemesById.get(schemeId);
};

const getDimensionName = (
  dimension: Dimension,
  customFields: CustomFieldDefinition[],
): DimensionName => {
  switch (dimension.type) {
    case 'customField':
      return {
        type: 'customField',
        name:
          customFields.find(({ id }) => id === dimension.customFieldId)?.name ||
          'Custom Field',
      };
    case 'spendType':
    case 'costCenter':
    case 'team':
    case 'expenseCategory':
      return { type: dimension.type };
    default:
      throw new TypeGuardError(dimension, 'Unknown dimension type');
  }
};

const registerExportedApprovalScheme = (
  approvalScheme: ApprovalSchemeWithMemberNames | undefined,
  exportedApprovalSchemes: ApprovalSchemeExport[],
  t: TGlobalFunctionTyped,
) => {
  if (!approvalScheme) {
    return undefined;
  }
  const approvalSchemeExport = exportApprovalScheme(
    exportedApprovalSchemes.length,
    approvalScheme,
    t,
  );
  exportedApprovalSchemes.push(approvalSchemeExport);
  return approvalSchemeExport;
};

const buildBranch = (
  node: CustomNode,
  nodes: CustomNode[],
  edges: Edge[],
  customFields: CustomFieldDefinition[],
  approvalSchemesById: Map<string, ApprovalSchemeWithMemberNames>,
  exportedApprovalSchemes: ApprovalSchemeExport[],
  t: TGlobalFunctionTyped,
): ApprovalWorkflowBranchExport | undefined => {
  const outgoingEdges = edges.filter(
    (edge) => edge.source === node.id && edge.id !== 'initialEdge',
  );
  const childNodes: CustomNode[] = outgoingEdges
    .map((edge) => nodes.find((n) => n.id === edge.target))
    .filter((child) => child?.type !== 'newConditionNode')
    .filter(isNotNullable);

  const [firstChildNode] = childNodes;
  if (!firstChildNode) {
    return undefined;
  }

  const dimension =
    firstChildNode.type === 'baseNode'
      ? firstChildNode.data.dimension
      : undefined;

  if (!dimension) {
    return undefined;
  }

  return {
    dimension: getDimensionName(dimension, customFields),
    nodes: childNodes.map((childNode) => {
      const childBranch = buildBranch(
        childNode,
        nodes,
        edges,
        customFields,
        approvalSchemesById,
        exportedApprovalSchemes,
        t,
      );
      const childNodeData =
        childNode.type === 'baseNode' ? childNode.data : undefined;
      if (!childNodeData) {
        return {
          values: null,
          ...(childBranch ? { branch: childBranch } : {}),
        };
      }
      const resolvedScheme = getApprovalSchemeForNode({
        schemeId: childNodeData.schemeId,
        approvalScheme: childNodeData.approvalScheme,
        approvalSchemesById,
      });
      return {
        values: childNodeData.values?.map((value) => value.name) ?? null,
        approvalScheme: registerExportedApprovalScheme(
          resolvedScheme,
          exportedApprovalSchemes,
          t,
        ),
        ...(childBranch ? { branch: childBranch } : {}),
      };
    }),
  };
};

export type ExportParameters = {
  nodes: CustomNode[];
  edges: Edge[];
  customFields: CustomFieldDefinition[];
  approvalSchemes: ApprovalSchemeWithMemberNames[];
  t: TGlobalFunctionTyped;
};

export const exportApprovalWorkflow = ({
  nodes,
  edges,
  customFields,
  approvalSchemes,
  t,
}: ExportParameters): ApprovalWorkflowExport | null => {
  const standardApprovalFlowNode = nodes.find(
    (node) => node.type === 'standardApprovalFlowNode',
  );
  const rootNode = nodes.find((node) => node.type === 'labelNode');
  if (!standardApprovalFlowNode || !rootNode) {
    return null;
  }

  const approvalSchemesById = new Map(
    approvalSchemes.map((scheme) => [scheme.id, scheme]),
  );
  const defaultScheme = getApprovalSchemeForNode({
    schemeId: standardApprovalFlowNode.data.schemeId,
    approvalScheme: standardApprovalFlowNode.data.approvalScheme,
    approvalSchemesById,
  });

  const exportedApprovalSchemes: ApprovalSchemeExport[] = [];
  const defaultApprovalScheme = registerExportedApprovalScheme(
    defaultScheme,
    exportedApprovalSchemes,
    t,
  );

  return {
    approvalSchemes: exportedApprovalSchemes,
    defaultApprovalScheme,
    branch: buildBranch(
      rootNode,
      nodes,
      edges,
      customFields,
      approvalSchemesById,
      exportedApprovalSchemes,
      t,
    ),
  };
};
